import React from 'react';
import { Link } from 'react-router-dom';
import { getUserRole } from '../api';

export default function AccessDenied({ requiredRoles = [] }) {
  const currentRole = getUserRole() || 'brainiac';

  const roleDisplayNames = {
    admin: 'Admin',
    team_captain: 'Team Captain',
    brainiac: 'Brainiac'
  };

  return (
    <div className="access-denied-container">
      <div className="access-denied-content">
        {/* Animated Brain Character */}
        <div className="brain-character">
          <div className="brain-body">
            <div className="brain-face">
              <div className="brain-eyes">
                <div className="brain-eye left">
                  <div className="pupil"></div>
                </div>
                <div className="brain-eye right">
                  <div className="pupil"></div>
                </div>
              </div>
              <div className="brain-mouth"></div>
            </div>
            <div className="brain-wrinkles">
              <div className="wrinkle w1"></div>
              <div className="wrinkle w2"></div>
              <div className="wrinkle w3"></div>
            </div>
          </div>
          <div className="brain-shadow"></div>
          {/* Floating question marks */}
          <div className="floating-icons">
            <span className="float-icon q1">?</span>
            <span className="float-icon q2">?</span>
            <span className="float-icon lock">🔒</span>
          </div>
        </div>

        <h1 className="access-denied-title">Oops! This Area is Off-Limits</h1>

        <p className="access-denied-message">
          Looks like you need a different access level to explore this part of LRBrain.
        </p>

        <div className="access-denied-info">
          <div className="role-badge current">
            <span className="badge-label">You are</span>
            <span className="badge-value">{roleDisplayNames[currentRole]}</span>
          </div>
          {requiredRoles.length > 0 && (
            <div className="role-badge required">
              <span className="badge-label">Required</span>
              <span className="badge-value">
                {requiredRoles.map(r => roleDisplayNames[r]).join(' or ')}
              </span>
            </div>
          )}
        </div>

        <p className="access-denied-hint">
          Need access? Talk to your team lead or administrator!
        </p>

        <Link to="/" className="access-denied-button">
          <span className="button-icon">🏠</span>
          Back to Home
        </Link>
      </div>

      <style>{`
        .access-denied-container {
          min-height: calc(100vh - 70px);
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          padding: 2rem;
        }

        .access-denied-content {
          text-align: center;
          max-width: 480px;
        }

        /* Brain Character */
        .brain-character {
          position: relative;
          width: 160px;
          height: 160px;
          margin: 0 auto 2rem;
        }

        .brain-body {
          width: 120px;
          height: 100px;
          background: linear-gradient(145deg, #f8a5c2 0%, #f78fb3 50%, #e056a0 100%);
          border-radius: 60% 60% 55% 55% / 70% 70% 50% 50%;
          position: absolute;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          animation: brain-bounce 2s ease-in-out infinite;
          box-shadow:
            inset -8px -8px 20px rgba(0,0,0,0.1),
            inset 8px 8px 20px rgba(255,255,255,0.3);
        }

        @keyframes brain-bounce {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(-8px); }
        }

        .brain-face {
          position: absolute;
          top: 35%;
          left: 50%;
          transform: translateX(-50%);
          width: 80%;
        }

        .brain-eyes {
          display: flex;
          justify-content: center;
          gap: 20px;
          margin-bottom: 8px;
        }

        .brain-eye {
          width: 22px;
          height: 22px;
          background: white;
          border-radius: 50%;
          position: relative;
          animation: eye-blink 4s ease-in-out infinite;
        }

        .brain-eye .pupil {
          width: 10px;
          height: 10px;
          background: #1A4072;
          border-radius: 50%;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation: pupil-look 3s ease-in-out infinite;
        }

        @keyframes eye-blink {
          0%, 45%, 55%, 100% { transform: scaleY(1); }
          50% { transform: scaleY(0.1); }
        }

        @keyframes pupil-look {
          0%, 100% { transform: translate(-50%, -50%); }
          25% { transform: translate(-30%, -50%); }
          75% { transform: translate(-70%, -50%); }
        }

        .brain-mouth {
          width: 30px;
          height: 12px;
          border: 3px solid #1A4072;
          border-top: none;
          border-radius: 0 0 20px 20px;
          margin: 0 auto;
          animation: mouth-worry 2s ease-in-out infinite;
        }

        @keyframes mouth-worry {
          0%, 100% { transform: scaleX(1); }
          50% { transform: scaleX(0.8); }
        }

        .brain-wrinkles {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;
          border-radius: inherit;
        }

        .wrinkle {
          position: absolute;
          background: rgba(180, 60, 120, 0.3);
          border-radius: 50%;
        }

        .w1 {
          width: 40px;
          height: 20px;
          top: 10px;
          left: 10px;
          transform: rotate(-20deg);
        }

        .w2 {
          width: 35px;
          height: 18px;
          top: 15px;
          right: 15px;
          transform: rotate(25deg);
        }

        .w3 {
          width: 30px;
          height: 15px;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
        }

        .brain-shadow {
          width: 80px;
          height: 15px;
          background: rgba(0,0,0,0.1);
          border-radius: 50%;
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          animation: shadow-pulse 2s ease-in-out infinite;
        }

        @keyframes shadow-pulse {
          0%, 100% { transform: translateX(-50%) scale(1); opacity: 0.3; }
          50% { transform: translateX(-50%) scale(0.8); opacity: 0.2; }
        }

        /* Floating Icons */
        .floating-icons {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
        }

        .float-icon {
          position: absolute;
          font-size: 1.5rem;
          animation: float-around 3s ease-in-out infinite;
        }

        .q1 {
          top: 0;
          left: 10px;
          color: var(--lrbrain-primary);
          animation-delay: 0s;
        }

        .q2 {
          top: 10px;
          right: 10px;
          color: var(--lrbrain-accent);
          animation-delay: 1s;
        }

        .lock {
          top: -10px;
          left: 50%;
          transform: translateX(-50%);
          animation-delay: 0.5s;
        }

        @keyframes float-around {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
            opacity: 0.7;
          }
          50% {
            transform: translateY(-15px) rotate(10deg);
            opacity: 1;
          }
        }

        /* Text Content */
        .access-denied-title {
          font-family: var(--lrbrain-font-headings, 'Poppins', sans-serif);
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--lrbrain-navy, #1A4072);
          margin-bottom: 0.75rem;
          animation: title-appear 0.6s ease-out;
        }

        @keyframes title-appear {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .access-denied-message {
          font-size: 1.1rem;
          color: var(--lrbrain-text-light, #64748B);
          margin-bottom: 1.5rem;
          line-height: 1.6;
          animation: message-appear 0.6s ease-out 0.2s both;
        }

        @keyframes message-appear {
          from {
            opacity: 0;
            transform: translateY(15px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Role Badges */
        .access-denied-info {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          animation: badges-appear 0.6s ease-out 0.3s both;
        }

        @keyframes badges-appear {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .role-badge {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0.75rem 1.25rem;
          border-radius: 12px;
          min-width: 120px;
        }

        .role-badge.current {
          background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
          border: 2px solid var(--lrbrain-primary, #1F93FE);
        }

        .role-badge.required {
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          border: 2px solid #f59e0b;
        }

        .badge-label {
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--lrbrain-text-light, #64748B);
          margin-bottom: 0.25rem;
        }

        .badge-value {
          font-weight: 600;
          color: var(--lrbrain-navy, #1A4072);
          font-size: 0.95rem;
        }

        .access-denied-hint {
          font-size: 0.9rem;
          color: var(--lrbrain-text-light, #64748B);
          margin-bottom: 2rem;
          animation: hint-appear 0.6s ease-out 0.4s both;
        }

        @keyframes hint-appear {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        /* Button */
        .access-denied-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.875rem 1.75rem;
          background: var(--lrbrain-primary, #1F93FE);
          color: white;
          text-decoration: none;
          font-weight: 600;
          font-size: 1rem;
          border-radius: 12px;
          transition: all 0.3s ease;
          animation: button-appear 0.6s ease-out 0.5s both;
          box-shadow: 0 4px 15px rgba(31, 147, 254, 0.3);
        }

        @keyframes button-appear {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .access-denied-button:hover {
          background: #1780e0;
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(31, 147, 254, 0.4);
        }

        .access-denied-button:active {
          transform: translateY(0);
        }

        .button-icon {
          font-size: 1.2rem;
        }

        /* Responsive */
        @media (max-width: 480px) {
          .access-denied-title {
            font-size: 1.4rem;
          }

          .access-denied-info {
            flex-direction: column;
            align-items: center;
          }

          .role-badge {
            width: 100%;
            max-width: 200px;
          }
        }
      `}</style>
    </div>
  );
}
